import { isLocalDevelopment, VITE_OVERRIDE_ACCESS_LEVELS } from "@src/env";
import { useState, useEffect } from "react";
import { useStorage } from "@src/global_functions/hooks";
import {
  permissionsClaimsOverridesAtom,
  PermissionsClaims,
  ROLE_BASED_PERMISSIONS_FEATURES,
} from "./permissionsClaims";
import { useAtom } from "jotai";
import classNames from "classnames";
import { createPortal } from "react-dom";
import { Button, Text } from "@src/straps/base";
import { useIsLeftNavCollapsed } from "@src/straps/base/navigation/LeftNav/state";

function PermissionsClaimsOverrideManagerContent() {
  const [overrides, setOverrides] = useAtom(permissionsClaimsOverridesAtom);
  const [isExpanded, setIsExpanded] = useState(false);
  const [didEffectRun, setDidEffectRun] = useState(false);
  const [savedOverrides, saveOverrides] = useStorage(
    "access-level-overrides",
    [] as PermissionsClaims,
    window.sessionStorage
  );

  const isLeftNavCollapsed = useIsLeftNavCollapsed();

  // Load saved overrides from session storage
  useEffect(() => {
    if (!overrides.length && !didEffectRun) {
      setDidEffectRun(true);
      setOverrides(savedOverrides);
    }
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overrides]);

  // Save changes back to session storage
  useEffect(() => {
    if (overrides) {
      saveOverrides(overrides);
    }
  }, [overrides, saveOverrides]);

  return createPortal(
    isExpanded ? (
      <div
        className={classNames(
          "fixed bottom-4 z-100 border  border-solid bg-white p-4 opacity-50 transition-[left,bottom] hover:opacity-100 hover:shadow-lg",
          {
            "border-straps-negative": !overrides.length,
            "border-straps-positive": overrides.length,
            "left-20": isLeftNavCollapsed,
            "left-72": !isLeftNavCollapsed,
          }
        )}
      >
        <div className="flex items-center justify-between pb-4">
          <Text variant="sa_t-14-700" className="mr-3">
            User Access Level Overrides
          </Text>
          <Button
            dataTestId="dev-only-close-feature-access-override"
            onClick={() => setIsExpanded(false)}
            noFrameVariant="combo-s"
            icon="close"
          />
        </div>
        {ROLE_BASED_PERMISSIONS_FEATURES?.map((feature) => {
          const access = overrides?.find(
            (override) => override.feature === feature
          )?.access;
          const accessLevels = (["read", "edit"] as const).map(
            (level) => [level, level === access] as const
          );
          return (
            <div
              className="flex items-center justify-between py-0.5"
              key={feature}
            >
              <Text variant="sb_t-12-500">{feature}</Text>
              <div className="flex items-center gap-1">
                {accessLevels.map(([level, isSelected]) => (
                  <button
                    onClick={() => {
                      setOverrides((prev) => {
                        if (isSelected)
                          return prev?.filter(
                            (override) => override.feature !== feature
                          );
                        const featureExists = prev.find(
                          (override) => override.feature === feature
                        );
                        if (featureExists) {
                          return prev.map((override) => ({
                            ...override,
                            access:
                              override.feature === feature
                                ? level
                                : override.access,
                          }));
                        }
                        return [...prev, { feature, access: level }];
                      });
                    }}
                    className={classNames("border border-solid", {
                      "border-straps-positive bg-straps-positive-bg":
                        isSelected,
                      "border-straps-accent-3 bg-straps-accent-3": !isSelected,
                    })}
                    key={level}
                  >
                    <Text
                      variant="sb_t-12-500"
                      className={classNames({
                        "!font-semibold": isSelected,
                      })}
                    >
                      {level}
                    </Text>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <div className="fixed bottom-4 left-16 z-100">
        <Button
          dataTestId="dev-only-expand-feature-access-override"
          rounded
          outline
          className={classNames("group/button  h-8 w-8 !border ", {
            "!border-straps-positive !bg-straps-positive-bg": overrides.length,
            "!border-straps-negative !bg-white": !overrides.length,
          })}
          onClick={() => setIsExpanded(true)}
        >
          <div className="opacity-50 transition-opacity group-hover/button:opacity-100">
            👤
          </div>
        </Button>
      </div>
    ),
    document.body
  );
}

export default function PermissionsClaimsOverrideManager() {
  if (!isLocalDevelopment || !VITE_OVERRIDE_ACCESS_LEVELS) return null;
  return <PermissionsClaimsOverrideManagerContent />;
}
